/**
 * English strings for the setup-guide SCORING step (PREC-3700).
 * Owned by the scoring-step package — other tour strings live in
 * enOnboardingTour.ts. Keys are addressed as onboardingTour.scoringStep.<...>.
 */
import { enOnboardingTourScoringIntro } from "./enOnboardingTourScoringIntro";

export const enOnboardingTourScoring = {
	pending: {
		title: "We're still building your Lead Scoring",
		body: "We're still building your Lead Scoring — you can continue with the other steps and come back.",
	},
	footer: {
		getStarted: "Get started",
		testScoring: "Test Scoring",
		addCeoScoring: "Add CEO scoring — continue",
		looksGoodContinue: "Looks good — continue",
		looksGoodContinueInvited: "Looks good, continue",
		analyzeChanges: "Analyze my changes",
		applyAndRetest: "Apply & re-test",
		connectSenders: "Connect senders",
		finishOnboarding: "Finish onboarding",
		gotItCheck: "Got it — check my leads",
		nextGroup: "Looks right — next group",
		theseAreMyCustomers: "These are my customers",
		noPartners: "No partners — continue",
		nextPartnerGroup: "Next partner group",
		theseAreMyPartners: "These are my partners",
		nextRole: "Looks right — next role",
		theseAreMyBuyers: "These are my buyers",
		nextCriterion: "Looks right — next criterion",
		changeLaterTooltip:
			"Nothing is final: you can add, edit or delete your segments at any time later in the platform.",
	},
	customerGroups: {
		pillsLabel: "Your customer groups",
		position: "Customer group {{current}} of {{total}}",
		unnamed: "Unnamed group",
		nameLabel: "Customer group name",
		namePlaceholder: "e.g. Fintech & Financial Technology",
		descriptionLabel: "Customer group description",
		descriptionPlaceholder:
			"Describe this kind of customer in one or two sentences — what they do, how big they are, what makes them a fit.",
		add: "Add another group",
		remove: "This isn't a customer group — remove it",
	},
	partners: {
		no: "No — customers only",
		noDesc: "Everyone in your market is a potential buyer.",
		yes: "Yes — we have partners",
		yesDesc: "They'll be qualified separately, never mixed into your buyer scoring.",
		pillsLabel: "Your partner groups",
		position: "Partner group {{current}} of {{total}}",
		unnamed: "New partner group",
		nameLabel: "Partner group name",
		namePlaceholder: "e.g. Tax Advisors & Auditors",
		descriptionLabel: "Partner group description",
		descriptionPlaceholder:
			"Describe this kind of partner — who they are and why they'd refer business to you.",
		add: "Add another partner group",
		remove: "Remove this group",
		confirmOffTitle: "Turn partners off?",
		confirmOffBody:
			"Your partner groups stop being scored and are removed from every buyer role they're linked to.",
		confirmOffCta: "Turn off",
		confirmOffCancel: "Cancel",
	},
	exclusions: {
		investorsTitle: "Investors",
		investorsDesc: "Track investors as targets or partners — pick which kinds matter to you.",
		investorsSelect: "Which investor types are relevant? (at least one)",
		investorsOffHint: "Investors are off — turn them on to pick investor types.",
		competitorsTitle: "Competitors",
		competitorsDesc: "Describe who competes with you, so we can tell them apart from prospects.",
		competitorsPlaceholder: "Companies offering a similar service to the same buyers…",
		neverContactTitle: "Never contact",
		neverContactDesc:
			"Kinds of company that can never become a customer — a structural exclusion, not your blacklist.",
		neverContactHint:
			"Describe the types to filter out before they are ever scored: service providers and agencies that resell what you do, industries you stay out of (tobacco, gambling, defence), public authorities, or companies that only sell to consumers. A list of company names does not work here — this is about whole categories. Individual companies and people you never want contacted belong on your Blacklist, and named competitors on your Competitor list; both live inside Signalist and can be filled later.",
		neverContactPlaceholder:
			"e.g. agencies reselling our service, tobacco and gambling, B2C-only companies…",
	},
	buyerRoles: {
		pillsLabel: "Your buyer roles",
		position: "Buyer role {{current}} of {{total}}",
		unnamed: "New buyer role",
		nameLabel: "Buyer role name",
		namePlaceholder: "e.g. Finance & Accounting Lead",
		descriptionLabel: "Buyer role description",
		descriptionPlaceholder:
			"Describe this role — what they own and why your offer matters to them.",
		jobTitles: "Job titles",
		jobTitlesPlaceholder: "Type a job title — pick a suggestion or press Enter…",
		similarTitles: "+ similar titles, matched automatically",
		butNever: "But never",
		butNeverPlaceholder: "e.g. Assistant to CEO — titles to skip",
		relevantFor: "Relevant for",
		relevantForOn:
			"Considered — we look for this buyer role at {{group}}. Click to stop considering it.",
		relevantForOff:
			"Not considered — this buyer role is skipped at {{group}}. Click to consider it.",
		add: "Add another buyer role",
		remove: "This isn't a buyer role — remove it",
	},
	grading: {
		switchedOff: "Switched off — this doesn't affect the score.",
		add: "Add…",
		addDisqualifying: "Nothing disqualifies — add to change that",
		pillsLabel: "Scoring criteria",
		pills: {
			HQ_COUNTRY: "Company HQ",
			INDUSTRY: "Industries",
			EMPLOYEE_SIZE: "Company size",
			STRATEGIC_FIT: "Strategic Fit",
			PERSON_LOCATION: "Person location",
		},
		tiers: {
			3: "+3 points",
			2: "+2 points",
			1: "+1 point",
			0: "Disqualify",
		},
		location: {
			title: "Do you want to score by where a company is based?",
			hint: "The headquarter country of the company.",
			note: '"Rest of the world" stands for every country you haven\'t listed above. Drag it into another bucket to score those countries instead of disqualifying them, or remove it to leave them at 0 points.',
		},
		industry: {
			title: "Do you want to score by industry?",
			hint: "Off by default — switch it on if industry should affect the score.",
			note: "Industries not listed anywhere simply score 0 points.",
		},
		size: {
			title: "Do you want to score by company size?",
			hint: "Assign LinkedIn's company-size ranges to points — each range can only sit in one bucket.",
			note: "Ranges you don't assign anywhere just score 0 points.",
			// PREC-3896: the note above states the rule; a user who assigned the four ranges they
			// recognise needs to be told which five they left at zero.
			warning_one: "{{ranges}} is not assigned to any tier, so leads that size score 0 points.",
			warning_other:
				"{{count}} ranges are not assigned to any tier, so leads those sizes score 0 points: {{ranges}}.",
			placeholder: "Select company sizes…",
			disqualifyPlaceholder: "Select sizes to disqualify…",
			inTier: "in {{tier}}",
		},
		fit: {
			title: "Do you want an AI agent to judge strategic fit?",
			badge: "AI agent",
			hint: "An AI agent reads each company and judges fit beyond the hard numbers — in your words.",
			disqualifyLabel: "Disqualify",
			scoreHigherLabel: "Score higher",
		},
		personLocation: {
			title: "Do you want to score by the person's location?",
			hint: "Where the person themselves is based — often, but not always, the company's country.",
			copyFromHq: "Copy from company HQ",
			note: '"Rest of the world" stands for every location you haven\'t listed above. Drag it into another bucket to score those locations instead of disqualifying them, or remove it to leave them at 0 points.',
		},
	},
	ceo: {
		question: "Run a separate scoring for CEOs & Managing Directors?",
		yes: "Yes — add CEO scoring",
		yesDesc: "CEOs & MDs at large companies are disqualified; at smaller ones they score normally.",
		no: "No — score everyone the same",
		noDesc: "CEOs & MDs keep the normal scoring, whatever the company size.",
		cutoffBefore: "Disqualify CEOs & MDs at companies of",
		cutoffAfter: "employees and up",
		cutoffLabel: "Disqualify CEOs & MDs from this company size up",
		cutoffNote:
			"Below that they keep the scoring you just set. Everyone who isn't a CEO or MD is unaffected either way.",
		active: "CEO scoring is on — disqualified from {{range}} employees up",
		activeBody:
			"Your workspace already runs a separate scoring for CEOs & Managing Directors. You can change the cutoff any time under Settings → Lead Scoring.",
		examples: {
			smallName: "Marcus Weber",
			smallDetail: "Founder & CEO · 85 employees — small company, the CEO is the buyer.",
			smallBadge: "Prio 1",
			largeName: "Felix Bauer",
			largeDetail:
				"CEO · 12,000 employees — enterprise, the CEO is out of reach. Your buyer there is a department lead.",
			largeBadge: "Disqualified",
		},
	},
	demo: {
		caption: {
			hover: "Hover any grade to see exactly why we gave it.",
			change: "Click it to correct it — and tell us why.",
			analyze: "We analyze every correction you make…",
			suggestion: "…and suggest scoring updates you accept or reject.",
		},
		changed: "changed",
		lead: {
			name: "Sofia Bergmann",
			role: "Head of Operations",
			company: "Klara Retail · 230 employees · Vienna, AT",
		},
		reasoning: {
			title: "Why this grade",
			companySize: "Company size — 230 employees",
			companySizePoints: "+2",
			location: "Company location — Vienna, Austria",
			locationPoints: "+3",
			role: "Buyer role — Operations Lead",
			rolePoints: "+2",
		},
		change: {
			title: "Change this grade",
			reason: "Reason: operations leads decide this at our customers.",
		},
		analyzing: "Analyzing your corrections…",
		suggestion: {
			title: "Weight the buyer-role match higher",
			before: "A matching buyer role adds +2 to +3 points, weighted like the firmographics.",
			after: "Role match becomes the strongest criterion — you moved Sofia Bergmann to Prio 1.",
			accept: "Accept",
			reject: "Reject",
		},
	},
	summary: {
		title: "Lead Scoring is live",
		body: "Every new lead is now sorted into your groups and graded automatically. Everything you just reviewed stays editable under Settings → Lead Scoring.",
		customerGroups: "Customer groups:",
		grading: "Grading:",
		ceoScoring: "CEO scoring:",
		buyerRoles: "Buyer roles:",
		tuning: "Fine-tuned from:",
		aiManaged: "AI-managed",
		location: "location",
		industries: "industries",
		employees: "{{range}} employees",
		strategicFit: "Strategic Fit (AI)",
		ceoOff: "off — everyone scored the same",
		ceoOn: "on — CEOs & MDs disqualified from {{range}} employees up",
		segments_one: "{{count}} segment",
		segments_other: "{{count}} segments",
		partners_one: "{{count}} partner group",
		partners_other: "{{count}} partner groups",
		investors_one: "investors ({{count}} type)",
		investors_other: "investors ({{count}} types)",
		roles_one: "{{count}} persona segment",
		roles_other: "{{count}} persona segments",
		corrections_one: "{{count}} correction",
		corrections_other: "{{count}} corrections",
		accepted_one: "{{count}} accepted scoring update",
		accepted_other: "{{count}} accepted scoring updates",
	},
	intro: {
		flow: {
			found: "New lead found",
			sorted: "Sorted into your company segments",
			graded: "Scored",
		},
		grades: {
			p1: {
				badge: "Prio 1",
				name: "Perfect fit",
				desc: "Matches your ideal company and your ideal buyer. Contact these first.",
			},
			p2: {
				badge: "Prio 2",
				name: "Good fit",
				desc: "Most things match. Solid pipeline — right after your Prio 1s.",
			},
			p3: {
				badge: "Prio 3",
				name: "Worth a look",
				desc: "Could work, but something's off — wrong region, unusual role.",
			},
			dq: {
				badge: "Disqualified",
				name: "Filtered out",
				desc: "Clearly outside your market. Quietly removed so they never cost you time.",
			},
		},
		example: {
			...enOnboardingTourScoringIntro,
			eyebrow: "Example — how your leads come out",
			group: "your company segment",
			role: "your persona segment",
			leads: {
				match: {
					name: "Lena Hofmann",
					title: "CFO",
					company: "Fluxpay",
					employees: "120",
					location: "Berlin, Germany",
				},
				partial: {
					name: "Tobias Krause",
					title: "Marketing Manager",
					company: "Nordwind Logistics",
					employees: "40",
					location: "Hamburg, Germany",
				},
				miss: {
					name: "Ana Silva",
					title: "Yoga teacher",
					company: "Studio Luz",
					employees: "3",
					location: "Lisbon, Portugal",
				},
			},
		},
		whyTitle: "What you'll see on every lead:",
		whyBody:
			'its company segment, its persona segment, and a grade with a "why" — never a black box. The segments also power your Knowledge Base and filters later.',
		calloutTitle: "We already did the hard part.",
		calloutBody:
			"Groups, roles and grading rules were drafted from your website. The next screens are a review, not a form — fix what's wrong, confirm what's right.",
	},
	priority: {
		p1: "P1",
		p2: "P2",
		p3: "P3",
		dq: "DQ",
	},
	/**
	 * Headers of the tour's leads tables (the intro example and the scored sample)
	 * that the leads table itself does not translate — it hardcodes them.
	 */
	columns: {
		location: "Location",
		companySegment: "Company Segment",
		employees: "Employees",
		personaSegment: "Persona Segment",
		notCategorized: "Not categorized",
	},
	/** The only string left from the retired sample-list explainer. */
	overview: {
		enrichmentDone_one: "Enrichment complete — {{count}} lead scored and ready.",
		enrichmentDone_other: "Enrichment complete — {{count}} leads scored and ready.",
	},
	/** Shared chip-input strings — the segment forms themselves are gone. */
	segments: {
		chipPlaceholder: "Type and press Enter",
		removeChip: "Remove {{label}}",
		clearChips: "Remove all",
		addCustom: "Add as new",
	},
	testing: {
		loadingStep1: "Enriching the sample companies",
		loadingStep2: "Matching segments & personas",
		loadingStep3: "Assigning P1–P3 / DQ priorities",
		loadingStep4: "Writing the reasoning",
		analyzingStep1: "Reading your changes",
		analyzingStep2: "Finding patterns",
		analyzingStep3: "Drafting adjustments",
		retestingStep1: "Applying your accepted adjustments",
		retestingStep2: "Re-scoring the sample leads",
		resultsTitle_one: "Here's how your scoring rated {{count}} sample lead",
		resultsTitle_other: "Here's how your scoring rated {{count}} sample leads",
		retestedTitle: "Your re-scored sample leads",
		selfLearningCallout: "The Lead Scoring is self-learning and improves over time.",
		changeHint: "Try changing one result — the scoring learns from it",
		changedBadge: "Changed",
		stillBuilding:
			"Still finding and scoring more of your leads — they appear here as they come in.",
		unavailableTitle: "We couldn't put a sample together",
		unavailableBody:
			"Your scoring is set up and ready — we just couldn't find sample leads to demo it on right now. You can continue and activate it, then review real leads in your lists.",
		unavailableRetry: "Try again",
		retestedHint: "Re-tested with your updated scoring. Hover a grade to see the new reasoning.",
		changeAria: "Correct the grade for {{name}}",
		employees: "employees",
		rescoredBadge: "re-scored",
		change: "Change",
		columns: {
			lead: "Lead",
			seniority: "Seniority",
			personalLocation: "Personal location",
			company: "Company",
			industry: "Industry",
			employees: "Employees",
			companyLocation: "Company location",
			segments: "Segments",
			priority: "Priority",
		},
		changeModal: {
			title: "Change this result",
			priority: "Priority",
			companySegment: "Company segment",
			personaSegment: "Persona segment",
			reasonLabel: "Why this change? (optional)",
			reasonPlaceholder: "e.g. this role never buys from us…",
			save: "Save change",
			cancel: "Cancel",
		},
		suggestionsTitle: "Suggested adjustments from your changes",
		suggestionsIntro:
			"Based on your corrections, here's what we'd adjust. Review each one — nothing changes until you apply.",
		noSuggestions: "No adjustments needed — your scoring already matches your changes.",
		before: "Before",
		after: "After",
		accept: "Accept",
		reject: "Reject",
		accepted: "Accepted",
		rejected: "Rejected",
		reviewAllHint: "Review every suggestion to continue",
	},
};
